import {
  AssociationSection,
  MeterAssociationItem,
} from "@/features/Meters/types/Meters.type";
import { ROUTES } from "./routes.constants";
import { CustomSelectNewItem } from "@/components/extended/type";
import { MeterReadingDateRange } from "@/features/Meters/types/MeterReading.type";
import { getCSSVariable } from "../utils/utils";
import { DEFAULT_THEME_COLOR } from "./themeColors.constants";

const getReportAssociationRoute = (item: MeterAssociationItem): string => {
  const normalizedName = item.name.trim().toLowerCase();

  if (normalizedName.includes("enem12")) {
    return ROUTES.reportingSetup.enem12;
  }

  if (
    normalizedName.includes("nem12 quality flag") ||
    (normalizedName.includes("quality") && normalizedName.includes("flag"))
  ) {
    return ROUTES.reportingSetup.nem12QualityFlag;
  }

  if (normalizedName.includes("nem12")) {
    return ROUTES.reportingSetup.nem12;
  }

  if (normalizedName.includes("meter event")) {
    return ROUTES.reportingSetup.meterEvent;
  }

  if (
    normalizedName.includes("orion cumulative") ||
    normalizedName.includes("orion")
  ) {
    return ROUTES.reportingSetup.orionCumulative;
  }

  if (normalizedName.includes("mdn")) {
    return ROUTES.reportingSetup.mdn;
  }

  return ROUTES.reporting.view;
};

const getValidationAssociationRoute = (): string =>
  ROUTES.reportingSetup.validation;

export const RESPONSE_CODE = {
  success: 200,
  unauthorized: 401,
  forbidden: 403,
  notFound: 404,
  internalServerError: 500,
};

export const CHANGE_LOG_OPERATIONS = {
  INSERT: "INSERT",
  UPDATE: "UPDATE",
  DELETE: "DELETE",
} as const;

export const OPERATION_LABELS: Record<
  keyof typeof CHANGE_LOG_OPERATIONS,
  string
> = {
  [CHANGE_LOG_OPERATIONS.INSERT]: "Created",
  [CHANGE_LOG_OPERATIONS.UPDATE]: "Updated",
  [CHANGE_LOG_OPERATIONS.DELETE]: "Deleted",
};

export const PAGE_SIZE_OPTIONS = [
  { label: "5 Rows", value: "5" },
  { label: "10 Rows", value: "10" },
  { label: "25 Rows", value: "25" },
  { label: "50 Rows", value: "50" },
  { label: "100 Rows", value: "100" },
];

export const DEFAULT_DIRECTION: "asc" | "desc" = "asc";

export const SORTING = {
  asc: "asc",
  desc: "desc",
};

export const TIME_ZONE_OPTIONS = [
  // {
  //   label: "Select Timezone",
  //   value: "",
  // },
  {
    label: "Australia/Adelaide",
    value: "Australia/Adelaide",
  },
  {
    label: "Australia/Brisbane",
    value: "Australia/Brisbane",
  },
  {
    label: "Australia/Darwin",
    value: "Australia/Darwin",
  },
  {
    label: "Australia/Hobart",
    value: "Australia/Hobart",
  },
  {
    label: "Australia/Melbourne",
    value: "Australia/Melbourne",
  },
  {
    label: "Australia/Perth",
    value: "Australia/Perth",
  },
  {
    label: "Australia/Sydney",
    value: "Australia/Sydney",
  },
];

export const DEFAULT_TIMEZONE = "Australia/Adelaide";

export const VIEW_NOTIFICATION_RADIO = [
  { id: "both", htmlFor: "both", value: "both", label: "Both" },
  { id: "error", htmlFor: "error", value: "error", label: "Error" },
  { id: "success", htmlFor: "success", value: "success", label: "Success" },
];

export const TRY_AGAIN = "Something went wrong. Please try again.";

export const DEFAULT_ORDER_BY_FOR_MAPPED_METERS = "meter_id";

export const DEFAULT_ORDER_BY_FOR_TIMESTAMP = "timestamp";

export const DEFAULT_ORDER_BY_FOR_MAPPED_METERS_STANDARD = "meterId";

export const DEFAULT_ORDER_BY_FOR_MAPPED_METER_GROUPS = "group_name";

export const DEFAULT_ORDER_BY_FOR_MAPPED_METER_GROUPS_STANDARD = "groupName";

export const API_DATE_FORMAT = "YYYY-MM-DD";

export const API_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";

export const DATE_FORMAT = {
  dateTime: "DD-MM-YYYY HH:mm",
  date: "DD-MM-YYYY",
  time: "HH:mm",
  year: "YYYY",
  month: "MMM-YY",
  day: "DD-MM-YYYY",
};

export const SYSTEM_CONFIGURATION_KEYS = {
  decimalValue: "DECIMAL_VALUE",
  systemProfileConfiguration: "SYSTEM_PROFILE_CONFIGURATION",
  powerOutageReasonCode: "POWER_OUTAGE_REASON_CODE",
  nem12ViewerFilesLimit: "NEM12_VIEWER_FILES_LIMIT",
};

export const IS_USED_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

export const POSITION_OPTIONS = [
  { value: "Prefix", label: "Prefix" },
  { value: "Suffix", label: "Suffix" },
  { value: "Mid", label: "Mid" },
];

export const DEFAULT_ORDER = {
  meterId: "meter_id",
  timestamp: "timestamp",
  eventTimestamp: "event_timestamp",
  site: "site_name",
  name: "name",
};

export const METER_EVENT_DIALOG_TABLE_COLUMNS = [
  { key: "meter_property_number", label: "Meter Property Number" },
  { key: "meter_serial_number", label: "Meter Serial Number" },
  { key: "nmi", label: "NMI" },
  { key: "title", label: "Title" },
  { key: "source", label: "Source" },
  { key: "data", label: "Data" },
  { key: "event_timestamp", label: "Event Timestamp (UTC)" },
] as const;

export const Permissions = {
  view: 1,
  create: 2,
  update: 3,
  delete: 4,
  resync: 6,
};

export const DATE_RANGE_OPTIONS = [
  {
    label: "Today",
    value: "Today",
  },
  {
    label: "Yesterday",
    value: "Yesterday",
  },
  {
    label: "Last Week",
    value: "Last Week",
  },
  {
    label: "Last Month",
    value: "Last Month",
  },
  {
    label: "Last 30 Days",
    value: "Last 30 Days",
  },
  {
    label: "Last 3 Months",
    value: "Last 3 Months",
  },
  {
    label: "Current Month",
    value: "Current Month",
  },
  {
    label: "Current Year",
    value: "Current Year",
  },
  {
    label: "Last Year",
    value: "Last Year",
  },
  {
    label: "Custom",
    value: "Custom",
  },
];

export const TAB_PHYSICAL = "Physical Gate";
export const TAB_VIRTUAL = "Virtual Gate";

export const DURATION_PREVIEW = [
  {
    value: "daily",
    label: "Daily",
  },
  {
    value: "hourly",
    label: "Hourly",
  },
];

export const AGGREGATION_TYPE = [
  {
    value: "Cumulative",
    label: "Cumulative",
  },
  {
    value: "Instantaneous",
    label: "Instantaneous",
  },
  {
    value: "Interval",
    label: "Interval",
  },
];

export const LEGEND_ITEMS = [
  { label: "Complete Data", color: "#AFEAC3" },
  { label: "No Data", color: "#F09797" },
  { label: "Partial Data", color: "#F3CE9A" },
  { label: "Complete Cumulative Data", color: "#82DDA1" },
  { label: "Substituted Data", color: "#A3BEF8" },
  { label: "Gap Filled", color: "#a9a3f8" },
];

export const SUPERSET_URL = import.meta.env.VITE_SUPERSET_URL ?? "";

export const SUPERSET_STORAGE_KEYS = {
  authToken: "SupersetAuthToken",
  refreshToken: "SupersetRefreshToken",
  selectedDashboardId: "selectedDashboardId",
  dashboardMode: "supersetDashboardMode",
};

export const SUPERSET_DASHBOARD_MODES = {
  view: "view",
  edit: "edit",
};

export const INTERACTIVE_TILE_CLASS =
  "transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(17,17,17,0.12)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset";

export const CHUNK_RELOAD_KEY = "chunk-reload";

export const IOT_PAYLOAD_OPTIONS = [
  { label: "PulseToLoRAWAN", value: "pulsetolorawan" },
  { label: "GWF-EcotoLorawan", value: "ecotolorawan" },
  { label: "LoRaWANSensor", value: "lorawansensor" },
  { label: "NativeMBus", value: "nativembus" },
  { label: "PulseToMBus", value: "pulsetombus" },
  { label: "BACNet", value: "bacnet" },
  { label: "Modbus", value: "modbus" },
];

export const CALCULATION_FIELD_OPTIONS = [
  { label: "Difference", value: "difference" },
  { label: "Offset", value: "offset" },
];

export const METER_ASSOCIATION_SECTIONS: AssociationSection[] = [
  {
    key: "sites",
    title: "Sites",
    getRoute: () => ROUTES.management.sites,
  },
  {
    key: "meter_groups",
    title: "Meter Groups",
    getRoute: () => ROUTES.management.meterGroups,
  },
  {
    key: "virtual_meters",
    title: "Virtual Meters",
    getRoute: () => ROUTES.management.virtualMeters,
  },
  {
    key: "reports",
    title: "Reports",
    getRoute: getReportAssociationRoute,
  },
  {
    key: "validations",
    title: "Validations",
    getRoute: getValidationAssociationRoute,
  },
  {
    key: "meter_alarm_event",
    title: "Meter Alarm Events",
    getRoute: () => ROUTES.monitoring.deviceStatus,
  },
];

export const CHANGE_METER = "CHANGE_METER";

export const CHANGE_SETTINGS = "CHANGE_SETTINGS";

export const NONE = "NONE";

export const CUSTOM_PROPERTY_ADVANCE_ECO_STATION = "ADVANCEDECOSTATION";

export const IOT_PAYLOAD_ECOTOLORAWAN = "ecotolorawan";

export const REAL_TIME_DATA_EMPTY_VALUE = "-";

export const BRISBANE_TIMEZONE = "Australia/Brisbane";

export const REAL_TIME_DATE_TIME_FORMAT = "DD-MM-YYYY HH:mm:ss";

export const REAL_TIME_NON_CHANNEL_FIELDS = new Set([
  "deviceName",
  "meterElement",
  "time",
  "disconnectRelay",
  "comm",
  "kVADmdMaxTS",
]);

export const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  kVADmdMaxTimestamp: "kVA Dmd Max Timestamp",
  deviceSN: "Device SN",
};

export const TIME_UNITS = {
  month: "month",
  day: "day",
  year: "year",
  hour: "hour",
};

export const REASON_CODE = {
  79: "79",
};

export const ChartTypeOptions = [
  { label: "Bar", value: "bar" },
  { label: "Line", value: "line" },
];

export const CHANNEL_TYPE_OPTIONS: CustomSelectNewItem[] = [
  { label: "Cumulative", value: "Cumulative" },
  { label: "Instantaneous", value: "Instantaneous" },
  { label: "Interval", value: "Interval" },
];

export const METER_READING_DATE_RANGES: MeterReadingDateRange[] = [
  "Today",
  "Yesterday",
  "Last Month",
  "Current Month",
  "Current Year",
  "Last Year",
  "Custom",
];

export const UNKNOWN_METER_TYPE = "Unknown";

export const ADVANCED_ECO_VALIDATION_VIEWS = {
  customer: "customer",
  site: "site",
  meter: "meter",
  detail: "detail",
};

export const ADVANCED_ECO_VALIDATION_BREADCRUMB_ITEMS = {
  customer: "Advanced Eco Validation",
  site: "Sites",
  meter: "Meters",
  detail: "Detail",
};

export const METER_GROUP_CONFIRMATION_ADDITIONAL_TEXT =
  "This meter group will be removed from all the associated reports and alarm events.";

export const chartColor = DEFAULT_THEME_COLOR;

export const getDynamicPrimaryColor = (): string =>
  getCSSVariable("--primary", chartColor);

export const ALGO_ANOMALY_TYPE = {
  algorithm: "ALGO",
  anomaly: "ANOMALY",
};

export const ALGORITHM_DETAILS_DIALOG_TABS = {
  algorithm: "Algorithm",
  anomalyType: "Anomaly Type",
};

export const ANOMALY_READING_DETAIL_DIALOG_TABS = {
  overview: "Overview",
  surroundingReadings: "Surrounding Readings",
};

export const DEFAULT_COLUMN_MIN_WIDTH = 140;

export const MIN_RESIZE_WIDTH = 60;

export const MAX_RESIZE_WIDTH = 800;

export const MIN_MULTISELECT_INPUT_WIDTH = 24;

export const DEFAULT_VALUE_CONTAINER_GAP = 6;

export const MIN_TRUNCATED_CHIP_WIDTH = 68;

export const OVERFLOW_BADGE_VISIBLE_CLASS_NAME = "shrink-0 whitespace-nowrap border border-border rounded-[3px] px-1 text-[13px] text-foreground";
